/**
 * @fileoverview Prompt template for character-snapshotter-single-v2.
 */

export const prompt = `
You are an expert story analyst with a keen eye for character development and continuity.

## TASK INSTRUCTIONS:
Your task is to produce a comprehensive, end-of-chapter snapshot for ONE specific character: **{{{activeCharacterCurrentContext.name}}}**. Do not report on any other characters.

Downstream prose agents (chapter drafters, editors, dialogue critics) read this snapshot as a major source of information about the character. Your output must be a complete, standalone 360-degree picture of who this character is at the end of the chapter — **every snapshot field must be populated**.

You will receive THREE inputs about the character:
1. The character's **initial state** at the start of the story (canonical baseline).
2. The character's **previous-chapter state** (the running state going into this chapter).
3. The **current chapter content** (the only source for new events that occurred during this chapter).

### Per-field decision procedure

For EACH snapshot field, work through these cases **in order** and apply the first one that fits:

**Case A — Previous-chapter value is populated and NOTHING in the chapter constitutes a change:**
Reproduce the previous-chapter value **verbatim**. No paraphrasing, no summarizing, no condensing. Any form of re-wording counts as drift and is forbidden.

**Case B — Previous-chapter value is populated and the chapter exhibits a change:**
Update the field. Preserve elements of the previous value that were not contradicted.
- Physical: Update and/or swap. E.g., if a character gains a scar, keep the rest of the physical description intact and add the scar; if a character changes clothes, describe what they wear at the end of the chapter and what happened to the previous clothing.
- Personality: Merge. E.g., if a character is generally shy but shows courage and bravery, mention both of that in the snapshot.
- Knowledge: If a character learns something new, append it to the existing knowledge.

**Case C — Previous-chapter value is empty or "[not yet established]", but the initial state has a value:**
Interpret the initial-state value as the baseline, then apply Case A or B logic. Empty values in previous snapshots represent thinning of data, not deliberate removal — they should be healed, not propagated.

**Case D — Both initial state and previous-chapter value are empty (or "[not yet established]"):**
This chapter is your opportunity to establish the initial value for the attribute. Derive a value from observable evidence in the chapter — dialogue patterns, actions, descriptions, narrative voice, choices the character makes, what they reference or react to. This is analysis, not invention.

**Case E — Genuinely no signal anywhere:**
Only when Cases A, B, C and D all fail (no value in initial state, no value in previous-chapter state, AND the chapter provides zero signal for this field), write the literal sentinel **"[not yet established]"**. Do not write an empty string. Do not omit the field.

### Inference vs. invention

You should describe traits *demonstrated* by explicit descriptions and/or **{{{activeCharacterCurrentContext.name}}}'s** behavior in this chapter, even if no prior source spelled them out:
- **Physical** can be inferred from how they move and interact.
- **Voice** can be inferred from how they speak — vocabulary, sentence rhythm, formality, verbal tics, how they express emotion.
- **Knowledge** can be inferred from what they reference, recognize, or act upon.
- **Values** can be inferred from the choices they make, especially under pressure.
- **Personality** can be inferred from how they handle emotions, conflict, strangers, intimacy, etc.

You **must not** introduce backstory, off-page events, prior knowledge, or facts that contradict the inputs. You **must not** reference planned arcs or future events — you are backward-looking. Inference from observed behavior in the chapter is allowed; fabrication of unobserved history is not.

### Major character events

Separately, list the most important events, decisions, or revelations that happened to **{{{activeCharacterCurrentContext.name}}}** in this chapter. Each event should be a concise, one-sentence description. Only include events that ACTUALLY OCCUR in the written chapter and have a certain importance. Do not include events from prior chapters. If the character has no major events in this chapter, leave the array empty.

---

## PRIMARY TASK CONTEXT

<<<activeCharacterContext>>>

<<<activeCharacterCurrentContext>>>
{{#if (eq activeCharacterCurrentContext.targetChapterNumber 1)}}

NOTE: This is the first chapter of the story, so the PREVIOUS-CHAPTER STATE and the INITIAL STATE are the same baseline. Treat the two blocks above as a single source of truth for the character going into this chapter, rather than two independent inputs to reconcile.
{{/if}}

**CHAPTER TO ANALYZE**
{{{chapterContent}}}

## SECONDARY TASK CONTEXT

<<<storyContextLite>>>

## OUTPUT INSTRUCTIONS:
Every snapshot field listed below must be populated by applying the per-field decision procedure. Empty strings are forbidden. Use the sentinel **"[not yet established]"** only when Case E applies.

{{#each characterSnapshotFields}}
- **{{this.label}}** (key: {{this.key}}): {{{this.description}}}
{{/each}}

Provide your output in the required JSON format. The response should only refer to character **{{{activeCharacterCurrentContext.name}}}**.
`;
